import { locale } from '@/utils/utils'

/**
 * 1. 按照 Dayjs 的解析标记来决定 DD/MM/YYYY HH:mm:ss 的大小写，特别注意小写 dd 表示简写的星期几，一般用不到小写
 * 参考网址：https://dayjs.fenxianglu.cn/category/display.html#%E6%A0%BC%E5%BC%8F%E5%8C%96
 * 2. 各国日期格式参考：https://gist.github.com/mlconnor/1887156
 * 3. 各个属性的含义：
 * expires_at 是优惠券的有效期格式
 * year_mins、year_secs 分别用于展示精确到分钟或秒数的 24 小时制时间
 * dayjsKey 表示 Dayjs locale.json 支持的语言 key 值 https://cdn.jsdelivr.net/npm/dayjs@1/locale.json
 */
const DATE_CONFIG = {
  pt_BR: {
    dayjsKey: 'pt-br',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[Expira às] HH:mm [em] DD/MM/YYYY',
    birthday: 'DD [de] MMMM [de] YYYY',
  },
  in_ID: {
    dayjsKey: 'id',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[Kedaluwarsa pada] DD/MM/YYYY [pk.] HH:mm',
    birthday: 'DD MMMM YYYY',
  },
  ms_MY: {
    dayjsKey: 'ms-my',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[Tamat tempoh pada] HH:mm [pada] DD/MM/YYYY ',
    birthday: 'DD MMMM, YYYY',
  },
  en_PH: {
    dayjsKey: 'en',
    year_day: 'MM/DD/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'MM/DD',
    year_secs: 'MM/DD/YYYY HH:mm:ss',
    year_mins: 'MM/DD/YYYY HH:mm',
    expires_at: '[Expires at] HH:mm [on] MM/DD/YYYY ',
    birthday: 'MMMM DD, YYYY',
  },
  en_IN: {
    dayjsKey: 'en-in',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[Expires at] DD/MM/YYYY HH:mm',
    birthday: 'MMMM DD, YYYY',
  },
  hi_IN: {
    dayjsKey: 'en-in', // dayjs 中暂时没有印地语
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: 'DD/MM/YYYY HH:mm [तक वै]',
    birthday: 'MMMM DD, YYYY',
  },
  ru_RU: {
    dayjsKey: 'ru',
    year_day: 'DD.MM.YYYY',
    year_month: 'MM.YYYY',
    month_day: 'DD.MM',
    year_secs: 'HH:mm:ss, DD.MM.YYYY[(MCK)]',
    year_mins: 'DD.MM.YYYY HH:mm[(MCK)]',
    expires_at: '[Действителен до] HH:mm, DD.MM.YYYY[(MCK)]',
    birthday: 'DD.MM.YYYY',
  },
  en_US: {
    dayjsKey: 'en',
    year_day: 'MM/DD/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'MM/DD',
    year_secs: 'MM/DD/YYYY HH:mm:ss',
    year_mins: 'MM/DD/YYYY HH:mm',
    expires_at: '[Expires at] HH:mm [on] MM/DD/YYYY ',
    birthday: 'MMMM DD, YYYY',
  },
  es_ES: {
    dayjsKey: 'es',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[Expira en] HH:mm [el] DD/MM/YYYY',
    birthday: 'DD MMMM YYYY',
  },
  de_DE: {
    dayjsKey: 'de',
    year_day: 'DD.MM.YYYY',
    year_month: 'MM.YYYY',
    month_day: 'DD.MM',
    year_secs: 'DD.MM.YYYY HH:mm:ss',
    year_mins: 'DD.MM.YYYY HH:mm',
    expires_at: '[Läuft ab bei] HH:mm [um] DD.MM.YYYY [ab]',
    birthday: 'DD.MM.YYYY',
  },
  fr_FR: {
    dayjsKey: 'fr',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[Expire à] HH:mm [le] DD/MM/YYYY',
    birthday: 'D MMMM YYYY',
  },
  it_IT: {
    dayjsKey: 'it',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[Scade alle] HH:mm [del] DD/MM/YYYY',
    birthday: 'D MMMM YYYY',
  },
  th_TH: {
    dayjsKey: 'th',
    year_day: 'DD/MM/YYYY',
    year_month: 'MM/YYYY',
    month_day: 'DD/MM',
    year_secs: 'DD/MM/YYYY HH:mm:ss',
    year_mins: 'DD/MM/YYYY HH:mm',
    expires_at: '[หมดอายุที่] HH:mm [ในวันที่] DD/MM/YYYY',
    birthday: 'DD MMMM YYYY',
  },
  uz_UZ: {
    dayjsKey: 'uz',
    year_day: 'MM.DD.YYYY',
    year_month: 'MM.YYYY',
    month_day: 'DD.MM',
    year_secs: 'MM.DD.YYYY HH:mm:ss',
    year_mins: 'MM.DD.YYYY HH:mm',
    expires_at: 'MM.DD.YYYY [soat] HH:mm [da tugaydi] ',
    birthday: 'MMMM DD, YYYY',
  },
}

/**
 * 获取对应地区语言的日期显示格式
 */
function getDateConfig() {
  return DATE_CONFIG[locale()] || DATE_CONFIG.en_US
}

export { DATE_CONFIG, getDateConfig }
